/**
 * EventsIntro
 * -----------
 * The top of the public events list (/p/event), in the same two states as
 * PublicHeader:
 *
 *   guest      "Upcoming events" + subtitle, then a note that they are
 *              browsing as a guest. The header owns Log in / Sign up free,
 *              so the note carries no buttons — except on phones (≤520px),
 *              where the header folds them into its menu and the note's own
 *              pair is the only visible way in. Same breakpoint as
 *              publicHeader.css, so exactly one pair is ever on screen.
 *   signed in  "Hi <first name>, here's what's coming up" and, when they have
 *              one, a "Next up" card for their soonest registered event.
 *
 * "Next up" is the first of athlete_upcoming_events (registered, not over,
 * soonest first) from the feed's athlete summary. PublicHeader already reads
 * that same query for its registrations badge, so this card costs no request.
 *
 * The copy is fixed rather than read from the list view's page_title/subtitle:
 * for this table those hold its schema name ("Event") and description, which
 * are not written for visitors.
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import FEED_TOKENS from '../../theme/feedTokens';
import { eventStart } from '../../pages/feed/feedApi';
import { feedUserFrom, useAthleteSummary } from '../../pages/feed/useFeedData';
import { daysUntil, eventPath } from '../feed/EventCards';
import useSession from './useSession';
import '../../styles/eventsIntro.css';

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function clock(d) {
  const h = d.getHours();
  return `${h % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

function relativeDay(days) {
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `In ${days} days`;
}

function NextUpCard({ event }) {
  const s = eventStart(event);
  const d = Number.isFinite(s) ? new Date(s) : null;
  const days = daysUntil(event);
  const meta = [days !== null && relativeDay(days), d && clock(d), event.location].filter(Boolean).join(' · ');
  return (
    <Link to={eventPath(event)} className="ei-next" aria-label={`Next up: ${event.title}. ${meta}`}>
      {d && (
        <span className="ei-next-date" aria-hidden="true">
          <span>{MON[d.getMonth()].toUpperCase()}</span>
          <b>{String(d.getDate()).padStart(2, '0')}</b>
        </span>
      )}
      <span className="ei-next-txt">
        <b>Next up: {event.title}</b>
        {meta && <span>{meta}</span>}
      </span>
      <span className="ei-next-go" aria-hidden="true">
        View
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
      </span>
    </Link>
  );
}

export default function EventsIntro() {
  const userData = useSession();
  const signedIn = Boolean(userData);
  const user = feedUserFrom(userData);
  const { data: summary, isLoading } = useAthleteSummary(signedIn ? user.userId : null);
  const { pathname, search } = useLocation();
  const next = summary?.upcomingEvents?.[0] || null;

  if (signedIn) {
    return (
      <section className="ei" style={FEED_TOKENS}>
        <div className="ei-row">
          <div className="ei-head">
            <span className="ei-eyebrow">Events</span>
            <h1>Hi {user.firstName}, here's what's coming up</h1>
          </div>
          {isLoading && <span className="ei-next ei-skel" aria-hidden="true" />}
          {next && <NextUpCard event={next} />}
        </div>
      </section>
    );
  }

  return (
    <section className="ei" style={FEED_TOKENS}>
      <div className="ei-head">
        <span className="ei-eyebrow">Events</span>
        <h1>Upcoming events</h1>
        <p>Browse everything coming up. No account needed to look around.</p>
      </div>
      <div className="ei-guest" role="note">
        <span className="ei-guest-ico" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="8" r="4" /><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" /></svg>
        </span>
        <p className="ei-guest-txt">
          <b>You're browsing as a guest.</b> Log in to register for events and keep track of the ones you've joined.
        </p>
        <div className="ei-guest-cta">
          {/* Both carry ?next=, for the reason spelled out in PublicHeader:
              on phones this pair is the ONLY way in, so a sign-up link
              without it strands a new visitor on /feed. */}
          <Link to={`/sign-in?next=${encodeURIComponent(pathname + search)}`} className="ei-btn ei-btn-line">Log in</Link>
          <Link to={`/sign-up?next=${encodeURIComponent(pathname + search)}`} className="ei-btn ei-btn-primary">Sign up free</Link>
        </div>
      </div>
    </section>
  );
}
